import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { FileText, FlaskConical, Pencil, Printer, Plus, Search, Share2, Trash2 } from "lucide-react";

import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

import DataTable from "../components/common/DataTable";

import { erpApi, formatDate, formatTime, normalizeExternalUrl } from "../services/erpApi";

const statuses = [
  "collected",
  "sent_to_lab",
  "testing",
  "report_received",
  "printed",
  "delivered",
];

const todayDate = () => new Date().toISOString().slice(0, 10);

const fallbackLabTests = [
  { name: "Albumin", sample: "Blood" },
  { name: "Alkaline", sample: "Blood" },
  { name: "Allergy panel", sample: "Blood" },
  { name: "ALP", sample: "Blood" },
  { name: "AMA", sample: "Blood" },
  { name: "APTT", sample: "Blood" },
  { name: "ASO", sample: "Blood" },
  { name: "Bilirubin", sample: "Blood" },
  { name: "BLOOD GROUP", sample: "Blood" },
  { name: "BLOOD SUGER FASTING", sample: "Blood" },
  { name: "BLOOD SUGER POST PARANDIAL", sample: "Blood" },
  { name: "BLOOD SUGER RANDOM", sample: "Blood" },
  { name: "BT-CT", sample: "Blood" },
  { name: "BUN", sample: "Blood" },
  { name: "CALCIUM", sample: "Blood" },
  { name: "CBC", sample: "Blood" },
  { name: "Chloride", sample: "Blood" },
  { name: "CPK –MB", sample: "Blood" },
  { name: "CPK TOTAL (NAC)", sample: "Blood" },
  { name: "Creatinine", sample: "Blood" },
  { name: "CRP quantitative", sample: "Blood" },
  { name: "DENGUE CARD TEST IGM", sample: "Blood" },
  { name: "DENGUE NS -1 ANTIGEN", sample: "Blood" },
  { name: "ELECTROLYTES", sample: "Blood" },
  { name: "ESR", sample: "Blood" },
  { name: "FOOD,VEG,NONVEG", sample: "Blood" },
  { name: "GGT", sample: "Blood" },
  { name: "HBA1C", sample: "Blood" },
  { name: "HBsAG – Card Test", sample: "Blood" },
  { name: "HIV- RAPID TEST Card Test", sample: "Blood" },
  { name: "IGE", sample: "Blood" },
  { name: "IRON Profile", sample: "Blood" },
  { name: "KFT", sample: "Blood" },
  { name: "LDH", sample: "Blood" },
  { name: "LFT", sample: "Blood" },
  { name: "LIPID PROFILE", sample: "Blood" },
  { name: "M.P. ANTIGEN Card", sample: "Blood" },
  { name: "M.T. TEST", sample: "Blood" },
  { name: "MAGNESIUM", sample: "Blood" },
  { name: "ORAL GTT", sample: "Blood" },
  { name: "Phosphate", sample: "Blood" },
  { name: "PHOSPHORUS", sample: "Blood" },
  { name: "Potassium", sample: "Blood" },
  { name: "PROLACTIN", sample: "Blood" },
  { name: "PT-INR", sample: "Blood" },
  { name: "RA factor", sample: "Blood" },
  { name: "Ratio", sample: "Blood" },
  { name: "RETICULOCYTE COUNT", sample: "Blood" },
  { name: "Sgot", sample: "Blood" },
  { name: "Sgpt", sample: "Blood" },
  { name: "Smell", sample: "Blood" },
  { name: "Sodium", sample: "Blood" },
  { name: "T3", sample: "Blood" },
  { name: "T4", sample: "Blood" },
  { name: "Tablet", sample: "Blood" },
  { name: "Testosterone", sample: "Blood" },
  { name: "Total protien", sample: "Blood" },
  { name: "Total PSA", sample: "Blood" },
  { name: "TROP-T", sample: "Blood" },
  { name: "TSH", sample: "Blood" },
  { name: "TYPHIDOT IgG,IgM", sample: "Blood" },
  { name: "Urea", sample: "Blood" },
  { name: "Uric acid", sample: "Blood" },
  { name: "URINE FOR PREGNANCY", sample: "Urine" },
  { name: "URINE R/M", sample: "Urine" },
  { name: "VDRL", sample: "Blood" },
  { name: "VITAMIN B12", sample: "Blood" },
  { name: "VITAMIN D3", sample: "Blood" },
  { name: "WIDAL TEST", sample: "Blood" },
  { name: "Other Test", sample: "Blood" },
];

const sampleTypes = [
  "Blood",
  "Urine",
  "Swab",
  "Stool",
  "Semen",
  "Sputum",
  "Serum",
  "Plasma",
  "X-Ray / Scan",
  "Other",
];

const emptyTestRow = {
  test_name: "",
  sample_type: "",
  is_custom: false,
};

const emptyForm = {
  patient_id: "",
  tests: [{ ...emptyTestRow }],
  lab_name: "",
  collection_date: todayDate(),
  expected_date: "",
  report_pdf_url: "",
  report_text: "",
  remarks: "",
};

const getTests = (sample) => {
  if (sample?.tests?.length) {
    return sample.tests;
  }

  if (sample?.test_name) {
    return [
      {
        test_name: sample.test_name,
        sample_type: sample.sample_type || "",
      },
    ];
  }

  return [];
};

const getTestNames = (sample) =>
  getTests(sample)
    .map((test) => test.test_name)
    .filter(Boolean)
    .join(", ");

const inferSampleType = (name = "") => {
  const value = name.toLowerCase();
  if (value.includes("urine") || value.includes("pregnancy")) return "Urine";
  if (value.includes("stool") || value.includes("motion")) return "Stool";
  if (value.includes("swab")) return "Swab";
  if (value.includes("sputum")) return "Sputum";
  if (value.includes("semen")) return "Semen";
  if (value.includes("serum")) return "Serum";
  if (value.includes("plasma")) return "Plasma";
  if (value.includes("usg") || value.includes("ecg") || value.includes("x-ray") || value.includes("xray")) return "Other";
  return "Blood";
};

const reportMessage = (sample) =>
  `Hello ${sample.patient_name}, your ${getTestNames(sample)} report from ${
    sample.lab_name || "ATDC"
  } is ready.\nPDF: ${normalizeExternalUrl(sample.report_pdf_url)}`;

const reportDraft = (patient, testsText) =>
  [
    `Patient Name: ${patient.name || ""}`,
    `Mobile: ${patient.mobile || ""}`,
    `Tests: ${testsText || ""}`,
    "",
    "Findings:",
    "",
    "Impression:",
    "",
    "Advice:",
  ].join("\n");

export default function SamplesLabs() {
  const location = useLocation();
  const isExtractorPage = location.pathname === "/report-extract";
  const [samples, setSamples] = useState([]);
  const [patients, setPatients] = useState([]);
  const [services, setServices] = useState([]);

  const [form, setForm] = useState(emptyForm);

  const [statusFilter, setStatusFilter] = useState("");

  const [patientSearch, setPatientSearch] = useState("");

  const [open, setOpen] = useState(false);

  const [editingSample, setEditingSample] = useState(null);

  const load = useCallback(async () => {
    const [sampleRes, patientRes, serviceRes] = await Promise.all([
      erpApi.samples(statusFilter ? { status: statusFilter } : {}),
      erpApi.patients(),
      erpApi.services({ active: true, limit: 5000 }),
    ]);

    setSamples(sampleRes.data);
    setPatients(patientRes.data);
    setServices(serviceRes.data);
  }, [statusFilter]);

  useEffect(() => {
    load().catch(() => toast.error("Failed to load samples"));
  }, [load]);

  const filteredSamples = useMemo(() => {
    const term = patientSearch.trim().toLowerCase();
    if (!term) return samples;

    return samples.filter((sample) =>
      [
        sample.patient_name,
        sample.patient_mobile,
        sample.patient_id,
        sample.patient_uid,
        sample.id,
        sample.lab_name,
        sample.status,
        sample.collection_date,
        getTestNames(sample),
      ]
        .filter((value) => value !== undefined && value !== null)
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [patientSearch, samples]);

  const labTestOptions = useMemo(() => {
    const labServices = services.filter((service) =>
      ["lab", "diagnostic"].includes(String(service.category || "").toLowerCase()),
    );
    const source = labServices.length
      ? labServices.flatMap((service) => {
          const pricing = service.pricing?.length ? service.pricing : [{ provider: "Clinic" }];
          return pricing.map((price) => ({
            name: `${service.name}${price.provider ? ` (${price.provider})` : ""}`,
            baseName: service.name,
            provider: price.provider || "",
            sample: inferSampleType(service.name),
          }));
        })
      : fallbackLabTests;
    const seen = new Set();
    return source.filter((test) => {
      const key = test.name.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [services]);

  const addTestRow = () => {
    setForm((prev) => ({
      ...prev,
      tests: [...prev.tests, { ...emptyTestRow }],
    }));
  };

  const removeTestRow = (index) => {
    if (form.tests.length === 1) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      tests: prev.tests.filter((_, i) => i !== index),
    }));
  };

  const updateTest = (index, field, value) => {
    const updatedTests = [...form.tests];

    if (field === "test_name" && value === "__other__") {
      updatedTests[index] = {
        ...updatedTests[index],
        test_name: "",
        sample_type: updatedTests[index].sample_type || "Other",
        is_custom: true,
      };
      setForm((prev) => ({
        ...prev,
        tests: updatedTests,
      }));
      return;
    }

    updatedTests[index][field] = value;

    if (field === "test_name") {
      const selected = labTestOptions.find((t) => t.name === value);

      updatedTests[index].sample_type =
        selected?.sample || updatedTests[index].sample_type;
      updatedTests[index].is_custom = selected ? false : updatedTests[index].is_custom;
    }

    setForm((prev) => ({
      ...prev,
      tests: updatedTests,
    }));
  };

  const submit = async (event) => {
    event.preventDefault();

    try {
      if (!form.tests.length || !form.tests[0].test_name) {
        return toast.error("At least one test is required");
      }

      if (editingSample) {
        await erpApi.updateSample(editingSample.id, form);

        toast.success("Sample updated");
      } else {
        await erpApi.createSample(form);

        toast.success("Sample registered");
      }

      setOpen(false);

      setEditingSample(null);

      setForm({
        ...emptyForm,
        collection_date: todayDate(),
      });

      load();
    } catch {
      toast.error("Failed to register sample");
    }
  };

  const updateStatus = async (id, status) => {
    await erpApi.updateSample(id, { status });

    toast.success("Sample updated");

    load();
  };

  const openReport = (url) => {
    const reportUrl = normalizeExternalUrl(url);

    if (!reportUrl) {
      return toast.error("Report PDF URL missing");
    }

    window.open(reportUrl, "_blank", "noopener,noreferrer");
  };

  const openLetterheadReport = async (sample) => {
    try {
      const response = await erpApi.sampleLetterheadPdf(sample.id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      toast.error("ATDC letterhead report PDF could not open");
    }
  };

  const openStandardizedPdf = async (sample) => {
    if (!sample.standardized_report) {
      return toast.error("No standardized report found. Run 'Standardize' first.");
    }
    try {
      const response = await erpApi.sampleStandardizedPdf(sample.id);
      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(blobUrl, "_blank", "noopener,noreferrer");
      window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch {
      toast.error("Standardized PDF could not open");
    }
  };

  const standardizeSampleFromPdf = async (file) => {
    if (!editingSample) {
      return toast.error("Save the sample first, then standardize.");
    }
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("provider", form.lab_name || "External Lab");
    try {
      const response = await erpApi.standardizeSampleReport(editingSample.id, formData);
      const updatedSample = response.data.sample || {};
      const structured = response.data.structured_report || {};
      const src = response.data.extraction_source || "";
      setForm((prev) => ({
        ...prev,
        report_text: updatedSample.report_text || prev.report_text,
        lab_name: updatedSample.lab_name || prev.lab_name,
        status: updatedSample.status || prev.status,
        remarks: updatedSample.remarks || prev.remarks,
      }));
      setSamples((prev) =>
        prev.map((s) => (s.id === editingSample.id ? updatedSample : s))
      );
      const warnings = response.data.validation_warnings || [];
      const srcLabel = src === "gemini" ? "Gemini AI" : "Heuristic";
      toast.success(
        `Standardized via ${srcLabel}. ${warnings.length > 0 ? `${warnings.length} warning(s).` : ""}`
      );
    } catch (error) {
      toast.error(error.response?.data?.detail || "Standardization failed");
    }
  };

  const saveAndOpenLetterheadReport = async () => {
    if (!editingSample) {
      return toast.error("Pehle sample save karein");
    }
    try {
      const response = await erpApi.updateSample(editingSample.id, form);
      const updatedSample = response.data;
      setSamples((prev) => prev.map((sample) => (sample.id === editingSample.id ? updatedSample : sample)));
      setEditingSample(updatedSample);
      toast.success("Report matter saved on sample");
      await openLetterheadReport(updatedSample);
      return updatedSample;
    } catch {
      toast.error("ATDC letterhead PDF nahi ban payi");
      return null;
    }
  };

  const extractReportPdf = async (file) => {
    if (!editingSample) {
      return toast.error("Pehle sample save karein, phir lab PDF extract karein");
    }
    if (!file) return null;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("provider", form.lab_name || "External Lab");
    try {
      const response = await erpApi.extractSampleReport(editingSample.id, formData);
      const updatedSample = response.data.sample || {};
      setForm((prev) => ({
        ...prev,
        report_text: response.data.report_text || updatedSample.report_text || prev.report_text,
        lab_name: updatedSample.lab_name || prev.lab_name,
        status: updatedSample.status || prev.status,
        remarks: updatedSample.remarks || prev.remarks,
      }));
      setSamples((prev) => prev.map((sample) => (sample.id === editingSample.id ? updatedSample : sample)));
      toast.success("Lab PDF se report matter extract ho gaya");
      return response.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "PDF text extract nahi ho paya");
      return null;
    }
  };

  const getLetterheadReportFile = async (sample) => {
    const response = await erpApi.sampleLetterheadPdf(sample.id);
    const safeName = String(sample.patient_name || "patient").replace(/[^a-z0-9]+/gi, "_");
    return new File([response.data], `ATDC_lab_report_${safeName}.pdf`, { type: "application/pdf" });
  };

  const shareReport = (sample) => {
    const phone = String(sample.patient_mobile || "").replace(/\D/g, "");

    const whatsappNumber = phone.length === 10 ? `91${phone}` : phone;
    const text = `Hello ${sample.patient_name}, your ${getTestNames(sample)} report from ATDC is ready.`;

    getLetterheadReportFile(sample)
      .then((file) => {
        if (navigator.canShare?.({ files: [file] })) {
          return navigator.share({
            title: "ATDC Lab Report",
            text,
            files: [file],
          });
        }

        const reportUrl = normalizeExternalUrl(sample.report_pdf_url);
        const fallbackText = reportUrl ? `${text}\nPDF: ${reportUrl}` : `${text}\nPlease collect or attach the downloaded PDF.`;
        window.open(
          `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(fallbackText)}`,
          "_blank",
          "noopener,noreferrer",
        );
        return null;
      })
      .catch(() => {
        const reportUrl = normalizeExternalUrl(sample.report_pdf_url);
        if (!reportUrl) return toast.error("Report PDF share failed");

        window.open(
          `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(reportMessage(sample))}`,
          "_blank",
          "noopener,noreferrer",
        );
      });
  };

  const openAddSample = () => {
    setEditingSample(null);

    setForm({
      ...emptyForm,
      collection_date: todayDate(),
    });

    setOpen(true);
  };

  const openEditSample = (sample) => {
    setEditingSample(sample);

    setForm({
      patient_id: sample.patient_id || "",

      tests:
        getTests(sample).length > 0 ? getTests(sample) : [{ ...emptyTestRow }],

      lab_name: sample.lab_name || "",

      collection_date: (sample.collection_date || "").slice(0, 10),

      expected_date: sample.expected_date || "",

      report_pdf_url: sample.report_pdf_url || "",

      report_text: sample.report_text || "",

      remarks: sample.remarks || "",

      status: sample.status || "collected",
    });

    setOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="font-manrope text-2xl font-bold">
            {isExtractorPage ? "Report Extract" : "Samples & Labs"}
          </h1>

          <p className="text-sm text-hospital-text-muted">
            {isExtractorPage
              ? "Upload PMCH/Kshipra report PDF, auto extract matter and print it on ATDC letterhead."
              : "Track collection, lab transfer, testing, report receipt, print and delivery."}
          </p>
        </div>

        <Dialog
          open={open}
          onOpenChange={(nextOpen) => {
            setOpen(nextOpen);

            if (!nextOpen) {
              setEditingSample(null);

              setForm({
                ...emptyForm,
                collection_date: todayDate(),
              });
            }
          }}
        >
          <Button className="btn-primary" onClick={openAddSample}>
            <Plus className="mr-2 h-4 w-4" />
            New Sample
          </Button>

          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                {editingSample ? "Edit Lab Sample" : "Register Lab Sample"}
              </DialogTitle>
            </DialogHeader>

            <form
              onSubmit={submit}
              className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            >
              <div className="space-y-2 sm:col-span-2">
                <Label>Patient</Label>

                <Select
                  value={form.patient_id}
                  onValueChange={(value) =>
                    setForm({
                      ...form,
                      patient_id: value,
                    })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>

                  <SelectContent>
                    {patients.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name} - {p.mobile}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-4 sm:col-span-2">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Tests</Label>

                  <Button type="button" variant="outline" onClick={addTestRow}>
                    <Plus className="mr-2 h-4 w-4" />
                    Add More Test
                  </Button>
                </div>

                {form.tests.map((test, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-1 gap-3 rounded-xl border border-gray-200 p-4 sm:grid-cols-2"
                  >
                    <div className="space-y-2">
                      <Label>Test {index + 1}</Label>

                      {test.is_custom ? (
                        <Input
                          value={test.test_name}
                          placeholder="Test name manually type karein"
                          onChange={(event) =>
                            updateTest(index, "test_name", event.target.value)
                          }
                        />
                      ) : (
                        <Select
                          value={test.test_name}
                          onValueChange={(value) =>
                            updateTest(index, "test_name", value)
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select test" />
                          </SelectTrigger>

                          <SelectContent>
                            <SelectItem value="__other__">Other / Manual Test</SelectItem>
                            {labTestOptions.map((labTest) => (
                              <SelectItem key={labTest.name} value={labTest.name}>
                                {labTest.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      {test.is_custom && (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const updated = [...form.tests];
                            updated[index] = { ...emptyTestRow };
                            setForm({ ...form, tests: updated });
                          }}
                        >
                          Back to list
                        </Button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Sample Type</Label>

                      <Select
                        value={test.sample_type}
                        onValueChange={(value) =>
                          updateTest(index, "sample_type", value)
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select sample type" />
                        </SelectTrigger>

                        <SelectContent>
                          {sampleTypes.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {index > 0 && (
                      <div className="sm:col-span-2 flex justify-end">
                        <Button
                          type="button"
                          variant="destructive"
                          size="sm"
                          onClick={() => removeTestRow(index)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Lab Name</Label>

                <Input
                  value={form.lab_name}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      lab_name: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2">
                <Label>Sample Taken Date</Label>

                <Input
                  type="date"
                  value={form.collection_date}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      collection_date: e.target.value,
                    })
                  }
                />
              </div>

              {editingSample && (
                <div className="space-y-2">
                  <Label>Status</Label>

                  <Select
                    value={form.status || "collected"}
                    onValueChange={(value) =>
                      setForm({
                        ...form,
                        status: value,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>

                    <SelectContent>
                      {statuses.map((status) => (
                        <SelectItem key={status} value={status}>
                          {status.replaceAll("_", " ")}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2 sm:col-span-2">
                <Label>Report PDF URL</Label>

                <Input
                  value={form.report_pdf_url}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      report_pdf_url: e.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <Label>ATDC Letterhead Report Matter</Label>
                    <p className="text-xs text-hospital-text-muted">
                      PMCH/Kshipra ki report se matter yahan type/paste karein. PDF ATDC letterhead par banegi.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const patient = patients.find((item) => item.id === form.patient_id) || {};
                      setForm({
                        ...form,
                        report_text: reportDraft(
                          patient,
                          form.tests.map((test) => test.test_name).filter(Boolean).join(", "),
                        ),
                      });
                    }}
                  >
                    Draft
                  </Button>
                  {editingSample && (
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById("source-lab-pdf-upload")?.click()}
                    >
                      Extract From Lab PDF
                    </Button>
                  )}
                  {editingSample && (
                    <Button
                      type="button"
                      variant="outline"
                      className="text-violet-700 border-violet-300 hover:bg-violet-50"
                      onClick={() => document.getElementById("standardize-pdf-upload")?.click()}
                      title="Upload lab PDF to AI-standardize and save to this sample"
                    >
                      <FlaskConical className="mr-2 h-4 w-4" />
                      Standardize PDF
                    </Button>
                  )}
                  {editingSample && (
                    <Button
                      type="button"
                      className="btn-primary"
                      onClick={saveAndOpenLetterheadReport}
                    >
                      Save & Open ATDC PDF
                    </Button>
                  )}
                </div>
                <Input
                  id="source-lab-pdf-upload"
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(event) => {
                    extractReportPdf(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
                <Input
                  id="standardize-pdf-upload"
                  type="file"
                  accept="application/pdf"
                  className="hidden"
                  onChange={(event) => {
                    standardizeSampleFromPdf(event.target.files?.[0]);
                    event.target.value = "";
                  }}
                />
                <Textarea
                  className="min-h-40"
                  value={form.report_text}
                  placeholder="Report result, findings, impression ya advice type karein..."
                  onChange={(event) =>
                    setForm({
                      ...form,
                      report_text: event.target.value,
                    })
                  }
                />
              </div>

              <div className="space-y-2 sm:col-span-2">
                <Label>Remarks</Label>

                <Input
                  value={form.remarks}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      remarks: e.target.value,
                    })
                  }
                />
              </div>

              <div className="flex justify-end gap-2 sm:col-span-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Cancel
                </Button>

                <Button className="btn-primary">
                  {editingSample ? "Update Sample" : "Save Sample"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {isExtractorPage && (
        <Card className="border border-emerald-200 bg-emerald-50">
          <CardContent className="p-4 text-sm text-emerald-900">
            <p className="font-semibold">Report Extract workflow</p>
            <p className="mt-1">
              Sample row me edit icon dabao, Lab Name me PMCH/Kshipra likho, phir
              <span className="font-semibold"> Extract From Lab PDF </span>
              se PDF upload karo. Matter automatically ATDC letterhead box me aa jayega.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="border border-gray-200">
        <CardContent className="p-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative w-full max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-hospital-text-muted" />
              <Input
                className="pl-9"
                placeholder="Search name, mobile, patient ID, test..."
                value={patientSearch}
                onChange={(event) => setPatientSearch(event.target.value)}
              />
            </div>

            <Select
              value={statusFilter || "all"}
              onValueChange={(value) =>
                setStatusFilter(value === "all" ? "" : value)
              }
            >
              <SelectTrigger className="max-w-xs">
                <SelectValue placeholder="Filter status" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>

                {statuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status.replaceAll("_", " ")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <DataTable
        title={`Sample Tracking (${filteredSamples.length})`}
        rows={filteredSamples}
        empty="No samples registered"
        columns={[
          {
            key: "patient_name",
            label: "Patient",
          },

          {
            key: "tests",
            label: "Tests",
            render: (row) => (
              <div className="flex flex-wrap gap-2">
                {getTests(row).map((test, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-hospital-primary/10 px-3 py-1 text-xs font-medium text-hospital-primary"
                  >
                    {test.test_name}
                  </span>
                ))}
              </div>
            ),
          },

          {
            key: "sample_type",
            label: "Sample Types",
            render: (row) => (
              <div className="flex flex-wrap gap-2">
                {getTests(row).map((test, index) => (
                  <span
                    key={index}
                    className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-700"
                  >
                    {test.sample_type}
                  </span>
                ))}
              </div>
            ),
          },

          {
            key: "lab_name",
            label: "Lab",
          },

          {
            key: "status",
            label: "Status",
            render: (row) => (
              <span className="capitalize">
                {row.status?.replaceAll("_", " ")}
              </span>
            ),
          },

          {
            key: "collection_date",
            label: "Sample Taken",
            render: (row) => formatDate(row.collection_date),
          },

          {
            key: "created_at",
            label: "Added At",
            render: (row) => (
              <div className="leading-tight">
                <span className="block">{formatDate(row.created_at)}</span>
                <span className="block text-xs text-hospital-text-muted">
                  {formatTime(row.created_at)}
                </span>
              </div>
            ),
          },

          {
            key: "action",
            label: "Actions",

            render: (row) => (
              <div className="flex items-center gap-2">
                <Select
                  value={row.status}
                  onValueChange={(value) => updateStatus(row.id, value)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>

                  <SelectContent>
                    {statuses.map((status) => (
                      <SelectItem key={status} value={status}>
                        {status.replaceAll("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Button
                  variant="ghost"
                  size="icon"
                  title="Open ATDC letterhead report"
                  onClick={() => openLetterheadReport(row)}
                >
                  <FileText className="h-4 w-4 text-hospital-primary" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  title={row.standardized_report ? "Open standardized hospital report" : "No standardized report yet — use Standardize PDF"}
                  onClick={() => openStandardizedPdf(row)}
                  className={row.standardized_report ? "" : "opacity-40"}
                >
                  <FlaskConical className={`h-4 w-4 ${row.standardized_report ? "text-violet-600" : "text-gray-400"}`} />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  title="Open source lab PDF"
                  onClick={() => openReport(row.report_pdf_url)}
                >
                  <Printer className="h-4 w-4 text-slate-700" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  title="Share on WhatsApp"
                  onClick={() => shareReport(row)}
                >
                  <Share2 className="h-4 w-4 text-emerald-700" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  title="Edit sample"
                  onClick={() => openEditSample(row)}
                >
                  <Pencil className="h-4 w-4 text-hospital-primary" />
                </Button>
              </div>
            ),
          },
        ]}
      />
    </div>
  );
}
